import { useEffect, useId, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { geocoderLang, searchAddresses, type GeoAddress } from '@dorixona/api/maps'
import { keys } from '../api/keys'
import { useMapKeySource } from '../api/mapsKey'
import { Icon } from './Icon'
import { MapView, type MapPoint } from './MapView'

export interface MapPickerProps {
  /** Подпись карты для читалки. */
  label: string
  point: MapPoint | null
  onPick: (point: MapPoint) => void
  height?: number
}

/** Координаты — до шести знаков, как у щелчка по карте. */
const round = (n: number) => Math.round(n * 1e6) / 1e6

/** Ищем с третьей буквы и когда набор замер — не на каждую клавишу. */
const MIN_QUERY = 3
const PAUSE_MS = 350

/** Где сейчас компьютер сотрудника; null — доступа не дали или точка не пришла. */
function currentLocation(): Promise<MapPoint | null> {
  const geo = navigator.geolocation as Geolocation | undefined
  if (!geo) return Promise.resolve(null)
  return new Promise((resolve) => {
    geo.getCurrentPosition(
      (position) => {
        resolve({ lat: round(position.coords.latitude), lng: round(position.coords.longitude) })
      },
      () => {
        resolve(null)
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 },
    )
  })
}

/**
 * Выбор точки на карте: поиск адреса (геокодер Яндекса, только Ташкент),
 * кнопка «Моё местоположение» поверх карты и щелчок по карте. Все три пути
 * отдают одну точку в `onPick`; найденное карта приближает до улиц.
 */
export function MapPicker({ label, point, onPick, height = 440 }: MapPickerProps) {
  const listId = useId()
  const keySource = useMapKeySource()
  const [query, setQuery] = useState('')
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const [locating, setLocating] = useState(false)
  const [notice, setNotice] = useState('')

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setTerm(query.trim())
    }, PAUSE_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [query])

  const found = useQuery({
    queryKey: keys.geoSearch(term),
    queryFn: () => searchAddresses(keySource, term, geocoderLang('ru')),
    enabled: term.length >= MIN_QUERY,
    staleTime: 60_000,
    retry: false,
  })
  const results = found.data ?? []
  const showList = open && term.length >= MIN_QUERY

  const take = (place: GeoAddress) => {
    setQuery(place.label)
    setOpen(false)
    setNotice('')
    onPick({ lat: round(place.lat), lng: round(place.lng) })
  }

  const locate = async () => {
    setLocating(true)
    setNotice('')
    const here = await currentLocation()
    setLocating(false)
    if (here) onPick(here)
    else setNotice('Не получилось определить местоположение — разрешите доступ к геолокации')
  }

  return (
    <div className="ui-mappick">
      <div
        className="ui-mappick-search"
        role="search"
        onBlur={(e) => {
          // Фокус ушёл из поиска и списка — список закрываем.
          if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false)
        }}
      >
        <div className="ui-search">
          <Icon name="i-search" />
          <input
            className="ui-input"
            type="search"
            role="combobox"
            placeholder="Улица, дом или ориентир в Ташкенте"
            aria-label="Найти место на карте"
            aria-autocomplete="list"
            aria-controls={listId}
            aria-expanded={showList}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setOpen(true)
            }}
            onFocus={() => {
              setOpen(true)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false)
              // Enter берёт первое найденное — не отправляет форму.
              if (e.key === 'Enter') {
                e.preventDefault()
                const first = results[0]
                if (first && term === query.trim()) take(first)
              }
            }}
          />
        </div>
        {showList ? (
          <div className="ui-mappick-pop" id={listId}>
            {found.isPending ? <div className="ui-mappick-note">Ищем…</div> : null}
            {found.isError ? (
              <div className="ui-mappick-note">Поиск не ответил — попробуйте ещё раз</div>
            ) : null}
            {found.isSuccess && results.length === 0 ? (
              <div className="ui-mappick-note">В Ташкенте такого не нашлось</div>
            ) : null}
            {results.length ? (
              <ul role="listbox" aria-label="Найденные места">
                {results.map((place) => (
                  <li key={`${place.lat}:${place.lng}:${place.label}`}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={false}
                      onClick={() => {
                        take(place)
                      }}
                    >
                      <Icon name="i-pin" size={18} />
                      <span>
                        <b>{place.label}</b>
                        {place.note ? <small>{place.note}</small> : null}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="ui-mappick-map">
        <MapView label={label} point={point} onPick={onPick} height={height} />
        <button
          type="button"
          className="ui-iconbtn ui-mappick-locate"
          aria-label="Моё местоположение"
          title="Моё местоположение"
          disabled={locating}
          aria-busy={locating}
          onClick={() => {
            void locate()
          }}
        >
          <Icon name="i-locate" />
        </button>
      </div>

      {notice ? (
        <span className="ui-hint" role="alert">
          {notice}
        </span>
      ) : null}
    </div>
  )
}
